<template>
  <MainLayout section="api">
    <div id="api_section">
        <h1 style="text-align: center">xrpintel Data API</h1>
        <hr/>

        <div style="background-color: white; border-radius: 5px;" class="p-3 card_icon">
          <p>The <b>xrpintel</b> API allows developers to access XRP Ledger data and statistics on a <b>real-time</b> and <b>historical</b> basis. The following document outlines the formal API specification.</p>

          <p>For performance purposes, xrpintel limits the amount and rate of data which can be retrieved. If you need additional historical data other than what is publically available please contact <a href="mailto:devnullproductions@gmail.com">Dev Null Productions</a> for a consultation.</p>

          <p>All public data and services are provided without warrenty or guarantees.</p>
        </div>

        <div class="mt-5">
          <b-tabs v-model="tab_index">
            <b-tab title="General" active class="p-4">
              <h3>API Access</h3>
              <hr/>

              <p>Entry Point: <a v-bind:href="entry_point"><b>{{entry_point}}</b></a></p>

              <p>Transfer Protocol: <b>HTTP GET</b></p>

              <p>Data Format: <b>JSON</b></p>

              <p>
                Example (<i>using <a href="https://en.wikipedia.org/wiki/CURL">curl</a>):</i>
              </p>

              <pre>
                curl "<a v-bind:href="entry_point_example_uri">{{entry_point_example_uri}}</a>"
              </pre>
            </b-tab>

            <b-tab title="Ledgers" class="p-4">
              <h3>Ledgers API</h3>
              <hr/>

              <p><b>/ledger</b> - the most recent ledger id</p>

              <pre class="ml-3">
curl <a v-bind:href="ledger_uri">{{ledger_uri}}</a>
  => {{current_ledger}}
              </pre>
            </b-tab>

            <b-tab title="Accounts">
              <h3>Accounts API</h3>
              <hr/>

              <p><b>/accounts/total</b> - total number of ledger accounts</p>
              <pre class="ml-3">
curl <a v-bind:href="total_accounts_uri">{{total_accounts_uri}}</a>
  => {{total_accounts}}</pre>

              <hr/>

              <p><b>/accounts/:page</b> - Account directory</p>
              <p>Params:</p>
              <ul>
              <li><b>:page</b> - Results page to return</li>
              <li><b>:num</b> - Number of results per page</li>
              <li><b>:sort</b> - Sort order, may be one of: <i>asc (ascending id), desc (descending id), created_asc, created_desc, balance_asc (xrp balance ascending), balance_desc (xrp balance descending)</i></li>
              </ul>

              <pre class="ml-3 mb-0">
curl <a v-bind:href="example_account_directory_uri">{{example_account_directory_uri}}</a>
              </pre>
              <renderjson v-bind:data="account_directory"
                          class="ml-3"
                          no_expand_hide />

              <hr/>

              <p><b>/account/:id/ancestry</b> - return list of account's parent, grandparent, great-grandparent, etc to origin</p>
              <p>Params:</p>
              <ul>
              <li><b>:id</b> - account id</li>
              </ul>

              <pre class="ml-3 mb-0">
curl <a v-bind:href="example_account_ancestry_uri">{{example_account_ancestry_uri}}</a>
              </pre>
              <renderjson v-bind:data="account_ancestry"
                          class="ml-3"
                          no_expand_hide />
            </b-tab>

            <b-tab title="Gateways">
              <h3>Gateways API</h3>
              <hr/>

              <p><b>/gateways/obligations</b> - return list of known gateway IOUs</p>

              <pre class="ml-3 mb-0">
curl <a v-bind:href="gateway_obligations_uri">{{gateway_obligations_uri}}</a>
              </pre>
              <renderjson v-bind:data="gateway_obligations"
                          class="ml-3"
                          no_expand_hide />

              <hr/>

              <p><b>/gateways/trust_lines</b> - return number of trust lines to known gateways</p>

              <pre class="ml-3 mb-0">
curl <a v-bind:href="gateway_trust_lines_uri">{{gateway_trust_lines_uri}}</a>
              </pre>
              <renderjson v-bind:data="gateway_trust_lines"
                          class="ml-3"
                          no_expand_hide />
            </b-tab>

            <b-tab title="Reports">
              <h3>Reports API</h3>
              <hr/>

              <p><b>/reports</b> - get list of available reports, mapped by period (in seconds)</p>

              <div class="inset">
                <p>Periods:</p>
                <ul>
                <li><b>300</b> - 5 minutes</li>
                <li><b>3600</b> - 1 hour</li>
                <li><b>86400</b> - 1 day</li>
                <li><b>604800</b> - 1 week</li>
                <li><b>2629746</b> - 1 month</li>
                </ul>
              </div>

              <pre class="ml-3 mb-0">
curl <a v-bind:href="reports_uri">{{reports_uri}}</a>
              </pre>
              <renderjson v-bind:data="reports_data"
                          class="ml-3 mb-5"
                          no_expand_hide />

              <hr/>

              <p><b>/report/:period/:id</b> - get report for specified period (and optional timestamp)</p>

              <div class="inset">
                <p>Additional Parameters:</p>

                <ul>
                  <li><b>metrics</b>: Comma seperated list of metrics which to retrieve (ex. <i>?metrics=txs,fees</i>)</li>
                  <li><b>subperiod</b>: Smaller period which to retrieve split out metrics (ex. <i>?subperiod=hour</i>)</li>
                </ul>
              </div>

              <pre class="ml-3 mb-0">
curl <a v-bind:href="example_period_report_uri">{{example_period_report_uri}}</a>
              </pre>
              <renderjson v-bind:data="period_report"
                          class="ml-3 mb-5"
                          no_expand_hide />

              <pre class="ml-3 mb-0">
curl <a v-bind:href="example_timestamp_report_uri">{{example_timestamp_report_uri}}</a>
              </pre>
              <renderjson v-bind:data="timestamp_report"
                          class="ml-3"
                          no_expand_hide />
            </b-tab>

            <b-tab title="Metrics" class="p-4">
              <h3>Report Metrics</h3>
              <hr/>

              <p>The following are metrics included in xrpintel reports. See <i>reports</i> for details on retrieving</p>

              <p><b>Note</b>: Not all metrics are included in every report period</p>

              <ul>
                <li v-for="(desc, metric) in metrics" :key="metric">
                  <b>{{metric}}</b>: {{desc}}
                </li>
              </ul>
            </b-tab>
          </b-tabs>
        </div>
    </div>
  </MainLayout>
</template>

<script>
// See note about VueJsonPretty in src/components/RenderLedger
import renderjson from './vendor/renderjson/renderjson.vue'

import MainLayout from './components/MainLayout.vue'
import config from './config'
import tags from './tags'

// FIXME: why aren't all metrics being returned?

const METRICS = {
  account_rate : "Average number of accounts created per ledger",
  accounts_by_parent : "Number of accounts created by each parent",
  deleted_accounts : "Number of deleted accounts",
  delivered_payments_by_currency : "Cumulative amount of currency delivered by the XRP Ledger",
  fees : "Total fees paid",
  ious : "Cumulative IOU values issued and redeemed by issuer/currency",
  ledger_close_time : "Average time between ledger validations/closure",
  ledgers_closed : "Total number of ledgers closed",
  new_accounts : "Total number of new accounts created",
  offers : "Total number of offers created and cancelled on the ledger",
  offers_by_currency_pair : "Total number of offers created for each currency pair",
  payments : "Total number of payments sent on the ledger",
  payments_by_currency : "Cumulative value of payments sent by currency",
  top_payers : "Top 5 payers on the XRP Ledger ranked by number of payments (with actual cumulative payments by currency)",
  top_payments : "Top payments on the XRP Ledger for each currency (by sender and destination)",
  tx_rate : "Average number of transactons per ledger",
  txs : "Total number of transactions",
  txs_by_result : "Number of transactions by result"
}

function get_ledger(vue){
  vue.$http.get(vue.ledger_uri)
           .then(function(response){
             vue.current_ledger = parseInt(response["body"])
           }, function(){});
}

function get_total_accounts(vue){
  vue.$http.get(vue.total_accounts_uri)
           .then(function(response){
             vue.total_accounts = parseInt(response["body"])
           }, function(){});
}

function get_parent_distribution(vue){
  vue.$http.get(vue.parent_distribution_uri)
           .then(function(response){
             vue.parent_distribution = response["body"];
           }, function(){});
}

function get_account_directory(vue){
  vue.$http.get(vue.example_account_directory_uri)
           .then(function(response){
             vue.account_directory = response["body"];
           }, function(){});
}

function get_account_ancestry(vue){
  vue.$http.get(vue.example_account_ancestry_uri)
           .then(function(response){
             vue.account_ancestry = response["body"];
           }, function(){});
}

function get_gateway_obligations(vue){
  vue.$http.get(vue.gateway_obligations_uri)
           .then(function(response){
             vue.gateway_obligations = response["body"];
           }, function(){});
}

function get_gateway_trust_lines(vue){
  vue.$http.get(vue.gateway_trust_lines_uri)
           .then(function(response){
             vue.gateway_trust_lines = response["body"];
           }, function(){});
}

function get_reports_data(vue){
  vue.$http.get(vue.reports_uri)
           .then(function(response){
             vue.reports_data = response["body"];
             get_timestamp_report(vue);
           }, function(){});
}

function get_period_report(vue){
  vue.$http.get(vue.example_period_report_uri)
           .then(function(response){
             vue.period_report = response["body"];
           }, function(){});
}

function get_timestamp_report(vue){
  vue.$http.get(vue.example_timestamp_report_uri)
           .then(function(response){
             vue.timestamp_report = response["body"];
           }, function(){});
}

export default {
  name: 'API',

  components: {
    MainLayout,
    renderjson
  },

  data : function(){
    return {
      tab_index : 0,
      tabs : ['api', 'ledgers', 'accounts', 'gateways', 'reports', 'metrics'],

      current_ledger : 0,

      total_accounts : 0,
      parent_distribution : {},
      account_directory : [],
      account_ancestry : [],

      gateway_obligations : [],
      gateway_trust_lines : [],

      reports_data : {},
      period_report : {},
      timestamp_report : {},

      metrics : METRICS
    };
  },

  computed : {
    entry_point : function(){
      return config.DATA_URI;
    },

    example_day : function(){
      var date = new Date();
      date.setDate(date.getDate() - 1);
      return (date.getYear() + 1900) + "-" + ("0" + (date.getMonth() + 1)).slice(-2) + "-" + ("0" + date.getDate()).slice(-2);
    },

    entry_point_example_uri : function(){
      return this.entry_point + "/report/day/" + this.example_day;
    },

    ledger_uri : function(){
      return this.entry_point + "/ledger"
    },

    total_accounts_uri : function(){
      return this.entry_point + "/accounts/total"
    },

    parent_distribution_uri : function(){
      return this.entry_point + "/accounts/parent_distribution";
    },

    example_account : function(){
      var accts = Object.keys(tags.builtin);
      return accts[Math.floor(Math.random() * accts.length)];
    },

    example_account_directory_uri : function(){
      return this.entry_point + "/accounts/1?num=10&sort=created_asc";
    },

    example_account_ancestry_uri : function(){
      return this.entry_point + "/account/" + this.example_account + "/ancestry";
    },

    gateway_obligations_uri : function(){
      return this.entry_point + "/gateways/obligations";
    },

    gateway_trust_lines_uri : function(){
      return this.entry_point + "/gateways/trust_lines";
    },

    reports_uri : function(){
      return this.entry_point + "/reports";
    },

    example_report_period : function(){
      return 300;
    },

    example_period_report_uri : function(){
      return this.entry_point + "/report/" + this.example_report_period;
    },

    example_report_timestamp : function(){
      if(!this.reports_data[this.example_report_period]) return {};
      var rt = this.reports_data[this.example_report_period];
      return rt[Math.floor(Math.random() * rt.length)];
    },

    example_timestamp_report_uri : function(){
      return this.entry_point + "/report/" + this.example_report_period +
                                       "/" + this.example_report_timestamp;
    }
  },

  created : function(){
    get_ledger(this);
    get_total_accounts(this);
    get_parent_distribution(this);
    get_account_directory(this);
    get_account_ancestry(this);
    get_gateway_obligations(this);
    get_gateway_trust_lines(this);
    get_reports_data(this);
    get_period_report(this);
  },

  mounted() {
    this.tab_index = this.tabs.findIndex(tab => '#' + tab === this.$route.hash)
  }
}
</script>

<style scoped>
#api_section{
  background: #eef1f6;
  padding: 30px;
  height: 100%;
}

.tab-pane{
  border-radius: 0px 0px 5px 5px;
  padding: 15px;
  background-color: white;
}

.inset{
  border: 1px solid black;
  border-radius: 5px;
  padding: 15px;
  margin: 20px;
  background-color: #DBDBDB;
}
</style>
